import { useCallback, useEffect, useRef, useState } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker from '../components/SchoolPicker';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const STARTERS = [
  'How engaged are our fans right now?',
  'Who are our most engaged fans?',
  'Which fans have gone quiet?',
  'What does gameday attendance look like?',
  'Where can we improve with fan engagement?',
  'How is our activation funnel doing?',
];

/* ---------------------------------------------------------------------------
 * The model replies in light markdown. Rendering it as React elements rather
 * than HTML keeps the answer un-injectable -- these strings are model output
 * shaped by row values from the database, so they are not trusted markup.
 * ------------------------------------------------------------------------ */

const renderInline = (text, keyPrefix) => {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`${keyPrefix}-b${i}`}>{token.slice(2, -2)}</strong>);
    } else {
      parts.push(
        <code key={`${keyPrefix}-c${i}`} className="insights-code">
          {token.slice(1, -1)}
        </code>
      );
    }
    last = match.index + token.length;
    i += 1;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const splitRow = line =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());

const isDivider = line => /^\s*\|?[\s:-]*-[\s|:-]*\|?\s*$/.test(line) && line.includes('-');

const renderMarkdown = text => {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let paragraph = [];
  let list = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    const key = `p${blocks.length}`;
    blocks.push(
      <p key={key} className="insights-paragraph">
        {renderInline(paragraph.join(' '), key)}
      </p>
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!list.length) return;
    const key = `ul${blocks.length}`;
    blocks.push(
      <ul key={key} className="insights-list">
        {list.map((item, idx) => (
          <li key={`${key}-${idx}`}>{renderInline(item, `${key}-${idx}`)}</li>
        ))}
      </ul>
    );
    list = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (line.includes('|') && i + 1 < lines.length && isDivider(lines[i + 1])) {
      flushParagraph();
      flushList();
      const header = splitRow(line);
      const body = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        body.push(splitRow(lines[i]));
        i += 1;
      }
      i -= 1;
      const key = `t${blocks.length}`;
      blocks.push(
        <div key={key} className="insights-table-wrap">
          <table className="insights-table">
            <thead>
              <tr>
                {header.map((cell, idx) => (
                  <th key={`${key}-h${idx}`}>{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, rIdx) => (
                <tr key={`${key}-r${rIdx}`}>
                  {row.map((cell, cIdx) => (
                    <td key={`${key}-r${rIdx}c${cIdx}`}>
                      {renderInline(cell, `${key}-r${rIdx}c${cIdx}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (/^#{1,6}\s+/.test(line)) {
      flushParagraph();
      flushList();
      const key = `h${blocks.length}`;
      blocks.push(
        <h4 key={key} className="insights-subhead">
          {renderInline(line.replace(/^#{1,6}\s+/, ''), key)}
        </h4>
      );
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      flushParagraph();
      list.push(line.replace(/^\s*[-*]\s+/, ''));
      continue;
    }

    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flushParagraph();
  flushList();
  return blocks;
};

/* Cycles while a request is in flight. We don't stream, so this describes the
   stages the request actually goes through rather than faking progress. */
const THINKING_STAGES = [
  'Reading the question',
  'Choosing metrics',
  'Querying the database',
  'Writing the answer',
];

const ThinkingIndicator = () => {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setStage(s => Math.min(s + 1, THINKING_STAGES.length - 1)),
      2600
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div className="insights-thinking" role="status" aria-live="polite">
      <span className="insights-spinner" aria-hidden="true" />
      <span className="insights-thinking-text">{THINKING_STAGES[stage]}…</span>
    </div>
  );
};

const relativeTime = iso => {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString();
};

const SuperuserInsights = () => {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [assistantAvailable, setAssistantAvailable] = useState(true);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState('');

  const [highlights, setHighlights] = useState(null);
  const [loadingHighlights, setLoadingHighlights] = useState(false);

  const [threads, setThreads] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);

  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);

  const logEndRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.api.get('/analytics/superuser/insights/metadata/');
        if (cancelled) return;
        const list = res.data.schools || [];
        setSchools(list);
        setAssistantAvailable(res.data.assistant_available !== false);
      } catch (err) {
        if (!cancelled) {
          setMetaError(
            err.response?.status === 403
              ? 'Superuser access required.'
              : 'Could not load schools. Try reloading.'
          );
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadThreads = useCallback(async school => {
    if (!school) {
      setThreads([]);
      return;
    }
    try {
      const res = await apiService.api.get('/analytics/superuser/insights/threads/', {
        params: { school_id: school },
      });
      setThreads(res.data.threads || []);
    } catch {
      setThreads([]);
    }
  }, []);

  const loadHighlights = useCallback(async school => {
    if (!school) {
      setHighlights(null);
      return;
    }
    setLoadingHighlights(true);
    try {
      const res = await apiService.api.get('/analytics/superuser/insights/highlights/', {
        params: { school_id: school },
      });
      setHighlights(res.data);
    } catch {
      setHighlights(null);
    } finally {
      setLoadingHighlights(false);
    }
  }, []);

  useEffect(() => {
    loadThreads(schoolId);
    loadHighlights(schoolId);
  }, [schoolId, loadThreads, loadHighlights]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, asking]);

  const startNew = () => {
    setThreadId(null);
    setTurns([]);
    setQuestion('');
  };

  const handleSchoolChange = school => {
    setSchoolId(school?.id || '');
    // Answers are school-specific, so carrying a thread across a switch would
    // let earlier numbers be read as if they described the new school.
    startNew();
  };

  const openThread = async id => {
    if (id === threadId || asking) return;
    setLoadingThread(true);
    try {
      const res = await apiService.api.get(`/analytics/superuser/insights/threads/${id}/`);
      const msgs = res.data.messages || [];
      const rebuilt = [];
      for (let i = 0; i < msgs.length; i += 1) {
        if (msgs[i].role !== 'user') continue;
        const answer = msgs[i + 1]?.role === 'assistant' ? msgs[i + 1] : {};
        rebuilt.push({
          question: msgs[i].content,
          reply: answer.content || '',
          steps: answer.steps || [],
          error: answer.error || '',
          pending: false,
        });
      }
      setTurns(rebuilt);
      setThreadId(id);
    } catch {
      setMetaError('Could not open that conversation.');
    } finally {
      setLoadingThread(false);
    }
  };

  const deleteThread = async (id, event) => {
    event.stopPropagation();
    // Hard delete with no undo, so make the click deliberate and name what goes.
    const thread = threads.find(t => t.id === id);
    const count = thread?.message_count ?? 0;
    const confirmed = window.confirm(
      `Delete "${thread?.title || 'this conversation'}"?\n\n` +
        `${count} question${count === 1 ? '' : 's'} and ${count === 1 ? 'its' : 'their'} ` +
        'answers will be permanently removed. This cannot be undone.'
    );
    if (!confirmed) return;
    try {
      await apiService.api.delete(`/analytics/superuser/insights/threads/${id}/`);
      setThreads(prev => prev.filter(t => t.id !== id));
      if (id === threadId) startNew();
    } catch {
      setMetaError('Could not delete that conversation.');
    }
  };

  const ask = useCallback(
    async text => {
      const trimmed = (text || '').trim();
      if (!trimmed || !schoolId || asking) return;

      setTurns(prev => [...prev, { question: trimmed, reply: '', steps: [], pending: true }]);
      setQuestion('');
      setAsking(true);

      try {
        const res = await apiService.api.post('/analytics/superuser/insights/ask/', {
          question: trimmed,
          school_id: schoolId,
          thread_id: threadId,
        });
        setTurns(prev => {
          const next = [...prev];
          next[next.length - 1] = {
            question: trimmed,
            reply: res.data.reply || '',
            steps: res.data.steps || [],
            error: res.data.error || '',
            pending: false,
          };
          return next;
        });
        if (res.data.thread) {
          setThreadId(res.data.thread.id);
          setThreads(prev => {
            const rest = prev.filter(t => t.id !== res.data.thread.id);
            return [res.data.thread, ...rest];
          });
        }
      } catch (err) {
        const message =
          err.response?.data?.error ||
          (err.response?.status === 403
            ? 'Superuser access required.'
            : 'The request failed. Try again.');
        setTurns(prev => {
          const next = [...prev];
          next[next.length - 1] = {
            question: trimmed,
            reply: '',
            steps: [],
            error: message,
            pending: false,
          };
          return next;
        });
      } finally {
        setAsking(false);
      }
    },
    [schoolId, asking, threadId]
  );

  const selectedSchool = schools.find(s => s.id === schoolId);

  if (loadingMeta) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          <h1 className="staff-page-header">AI Insights</h1>
          <p className="staff-page-subheader">
            Ask questions about one school's fan engagement. Answers come from live data.
          </p>

          {metaError && <div className="insights-banner insights-banner--error">{metaError}</div>}

          {!assistantAvailable && !metaError && (
            <div className="insights-banner insights-banner--warn">
              <strong>Assistant not configured.</strong> Set <code>ANTHROPIC_API_KEY</code> on the
              backend to enable it.
            </div>
          )}

          <div className="insights-school-bar">
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 320px' }}>
              <label className="form-label">
                School
              </label>
              <SchoolPicker
                schools={schools}
                value={selectedSchool || null}
                onChange={handleSchoolChange}
              />
            </div>
            {selectedSchool && (
              <p className="insights-scope-note">
                Every answer is scoped to {selectedSchool.name}. Switching schools starts a new
                conversation.
              </p>
            )}
          </div>

          {schoolId && loadingHighlights && (
            <div className="insights-highlights insights-highlights--loading">
              <span className="insights-spinner" aria-hidden="true" />
              <span>Reading {selectedSchool?.name || 'this school'}'s numbers…</span>
            </div>
          )}

          {schoolId && !loadingHighlights && highlights && (
            <div className="insights-highlights">
              <div className="insights-tiles">
                {highlights.tiles.map(t => (
                  <div key={t.label} className="insights-tile">
                    <span className="insights-tile-label">{t.label}</span>
                    <span className="insights-tile-value">{t.value.toLocaleString()}</span>
                    <span className="insights-tile-sub">{t.sub}</span>
                  </div>
                ))}
              </div>

              {highlights.observations.length > 0 && (
                <ul className="insights-observations">
                  {highlights.observations.map((o, i) => (
                    <li key={i} className={`insights-observation insights-observation--${o.tone}`}>
                      {o.text}
                    </li>
                  ))}
                </ul>
              )}

              {!highlights.has_data && (
                <p className="insights-no-data">
                  This school has no accounts yet, so there is nothing to summarise.
                </p>
              )}
            </div>
          )}

          {!schoolId ? (
            <div className="insights-empty">Please select a school</div>
          ) : (
            <div className="insights-layout">
              <aside className="insights-threads">
                <div className="insights-threads-head">
                  <span className="insights-threads-title">Conversations</span>
                  <button
                    type="button"
                    className="insights-new-btn"
                    onClick={startNew}
                    disabled={asking}
                  >
                    + New
                  </button>
                </div>

                {threads.length === 0 ? (
                  <p className="insights-threads-empty">
                    No saved conversations for this school yet.
                  </p>
                ) : (
                  <ul className="insights-thread-list">
                    {threads.map(t => (
                      <li key={t.id} className="insights-thread-row">
                        <button
                          type="button"
                          className={`insights-thread${
                            t.id === threadId ? ' insights-thread--active' : ''
                          }`}
                          onClick={() => openThread(t.id)}
                          disabled={asking}
                        >
                          <span className="insights-thread-title">{t.title}</span>
                          <span className="insights-thread-meta">
                            {t.message_count} question{t.message_count === 1 ? '' : 's'} ·{' '}
                            {relativeTime(t.updated_at)}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="insights-thread-delete"
                          aria-label={`Delete conversation: ${t.title}`}
                          onClick={e => deleteThread(t.id, e)}
                          disabled={asking}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </aside>

              <section className="insights-conversation">
                <div className="insights-log">
                  {loadingThread && <div className="insights-empty">Opening conversation…</div>}

                  {!loadingThread && turns.length === 0 && (
                    <div className="insights-empty">
                      <p style={{ marginTop: 0 }}>
                        Ask anything about {selectedSchool?.name}'s fans — engagement,
                        retention, attendance, referrals, rewards, spend.
                      </p>
                      <div className="insights-starters">
                        {STARTERS.map(s => (
                          <button
                            key={s}
                            type="button"
                            className="insights-starter"
                            onClick={() => ask(s)}
                            disabled={asking}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {turns.map((turn, idx) => (
                    <div key={idx} className="insights-turn">
                      <div className="insights-question">{turn.question}</div>

                      {turn.pending && <ThinkingIndicator />}

                      {turn.steps?.length > 0 && (
                        <div className="insights-steps">
                          {turn.steps.map((step, sIdx) => (
                            <span
                              key={sIdx}
                              className={`insights-step ${
                                !step.ok
                                  ? 'insights-step--error'
                                  : step.verified
                                  ? 'insights-step--verified'
                                  : 'insights-step--generated'
                              }`}
                              title={step.sql || step.error || ''}
                            >
                              {!step.ok
                                ? `failed: ${step.detail || step.tool}`
                                : step.verified
                                ? `${step.detail} · ${step.rows} rows`
                                : `generated SQL · ${step.detail}`}
                            </span>
                          ))}
                        </div>
                      )}

                      {turn.reply && (
                        <div className="insights-answer">{renderMarkdown(turn.reply)}</div>
                      )}

                      {turn.error && (
                        <div className="insights-banner insights-banner--error">{turn.error}</div>
                      )}
                    </div>
                  ))}
                  <div ref={logEndRef} />
                </div>

                <form
                  className="insights-composer"
                  onSubmit={e => {
                    e.preventDefault();
                    ask(question);
                  }}
                >
                  <input
                    className="form-input"
                    type="text"
                    placeholder={`Ask about ${selectedSchool?.name || 'this school'}…`}
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    disabled={asking || !assistantAvailable}
                  />
                  <button
                    type="submit"
                    className="primary-button"
                    disabled={asking || !question.trim() || !assistantAvailable}
                  >
                    {asking ? 'Asking…' : 'Ask'}
                  </button>
                </form>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserInsights;
